import React, { useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  IconButton,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import EventNotification, { Notification } from './EventNotification';
import ConnectionStatusIndicator from './ConnectionStatusIndicator';

interface MainNavbarProps {
  onMenuClick: () => void;
  title?: string;
  drawerWidth?: number;
}

const MainNavbar: React.FC<MainNavbarProps> = ({ onMenuClick, title, drawerWidth = 0 }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { authState, logout } = useAuth();
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
  
  // 알림 데이터 예시 (실제로는 API에서 받아오거나 상태 관리 라이브러리를 사용할 것)
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: '1',
      title: '위험 상황 감지',
      message: '시설 8번 구역에서 익수 위험 상황이 감지됨',
      type: 'error',
      timestamp: new Date(Date.now() - 5 * 60000).toISOString(),
      read: false,
      link: '/alerts/incident/123'
    },
    {
      id: '2',
      title: '수질 이상 감지',
      message: 'PH 수치 정상 범위 이탈',
      type: 'warning',
      timestamp: new Date(Date.now() - 30 * 60000).toISOString(),
      read: false,
      link: '/pools/water-quality'
    },
    {
      id: '3',
      title: '시스템 업데이트',
      message: '새로운 시스템 업데이트 있음',
      type: 'info',
      timestamp: new Date(Date.now() - 2 * 3600000).toISOString(),
      read: true,
      link: '/settings/updates'
    },
  ]);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
    navigate('/login');
  };

  const handleProfileClick = () => {
    handleProfileMenuClose();
    navigate('/settings/profile');
  };

  const handleNotificationClick = (notification: Notification) => {
    // 알림을 읽음으로 표시
    setNotifications(prev => 
      prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
    );
  };

  const handleMarkAllAsRead = () => {
    // 모든 알림을 읽음으로 표시
    setNotifications(prev => 
      prev.map(n => ({ ...n, read: true }))
    );
  };

  const handleClearAll = () => {
    // 모든 알림 제거
    setNotifications([]);
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        width: drawerWidth > 0 ? { md: `calc(100% - ${drawerWidth}px)` } : '100%',
        ml: drawerWidth > 0 ? { md: `${drawerWidth}px` } : 0,
        zIndex: (theme) => theme.zIndex.drawer + 1
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={onMenuClick}
          sx={{ mr: 2, display: { md: 'none' } }}
        >
          <MenuIcon />
        </IconButton>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          {title || '시설 안전 관제 시스템'}
        </Typography>

        {/* WebSocket 연결 상태 표시 */}
        <Box sx={{ mr: 2 }}>
          <ConnectionStatusIndicator showLabel />
        </Box>

        {/* EventNotification 컴포넌트 사용 */}
        <EventNotification 
          notifications={notifications}
          onNotificationClick={handleNotificationClick}
          onMarkAllAsRead={handleMarkAllAsRead}
          onClearAll={handleClearAll}
        />

        {/* 프로필 아이콘 */}
        <Tooltip title="계정 관리">
          <IconButton
            color="inherit"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            edge="end"
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.dark' }}>
              U
            </Avatar>
          </IconButton>
        </Tooltip>

        {/* 프로필 메뉴 */}
        <Menu
          id="menu-appbar"
          anchorEl={profileMenuAnchor}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(profileMenuAnchor)}
          onClose={handleProfileMenuClose}
        >
          <MenuItem onClick={handleProfileClick}>
            <ListItemIcon>
              <AccountCircleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>프로필</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/settings'); }}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>설정</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>로그아웃</ListItemText>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default MainNavbar; 